import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { chunkedUpload, TYPE_META, fmtSize } from './formationApi';

const BtnPrimary = ({ children, ...p }) => (
  <button {...p} className="h-10 px-4 rounded-ctrl bg-orange text-orange-on text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-opacity">{children}</button>
);
const BtnGhost = ({ children, ...p }) => (
  <button {...p} className="h-10 px-4 rounded-ctrl border border-border text-sm text-ink hover:bg-surface-2 transition-colors">{children}</button>
);

export function FamilleDialog({ open, famille, onClose, onSaved }) {
  const { api } = useAuth();
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNom(famille?.nom || ''); setDescription(famille?.description || ''); } }, [open, famille]);
  const save = async () => {
    setBusy(true);
    try {
      if (famille?.id) await api().put(`/formation/familles/${famille.id}`, { nom, description, ordre: famille.ordre });
      else await api().post('/formation/familles', { nom, description });
      toast.success('Famille enregistrée'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface" data-testid="famille-dialog">
        <DialogHeader><DialogTitle className="text-ink">{famille?.id ? 'Modifier la famille' : 'Nouvelle famille'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nom *</Label><Input value={nom} onChange={e => setNom(e.target.value)} placeholder="Ex. Présentation commerciale" data-testid="famille-nom" /></div>
          <div className="space-y-1.5"><Label>Descriptif</Label><Textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} data-testid="famille-description" /></div>
        </div>
        <DialogFooter>
          <BtnGhost onClick={onClose}>Annuler</BtnGhost>
          <BtnPrimary onClick={save} disabled={busy || !nom.trim()} data-testid="famille-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer</BtnPrimary>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const titleFromName = (name) => name.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();

const FilePick = ({ label, accept, file, current, onPick, testid, multiple = false, summary }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <label className="flex items-center gap-3 px-3 py-2.5 rounded-ctrl border border-dashed border-border cursor-pointer hover:bg-surface-2 transition-colors" data-testid={`${testid}-zone`}>
      <Upload className="w-4 h-4 text-text-3" />
      <span className="text-sm text-ink truncate flex-1">{summary || (file ? `${file.name} (${fmtSize(file.size)})` : current ? `Actuel : ${current}` : multiple ? 'Choisir un ou plusieurs fichiers…' : 'Choisir un fichier…')}</span>
      <input type="file" accept={accept} multiple={multiple} className="hidden"
        onChange={e => { onPick(multiple ? Array.from(e.target.files || []) : (e.target.files?.[0] || null)); e.target.value = ''; }} data-testid={testid} />
    </label>
  </div>
);

export function ItemDialog({ open, item, familles, defaultFamilleId, onClose, onSaved }) {
  const { api } = useAuth();
  const [f, setF] = useState({});
  const [main, setMain] = useState(null);
  const [pptx, setPptx] = useState(null);
  const [showPptx, setShowPptx] = useState(false);
  const [progress, setProgress] = useState(null);
  const [batch, setBatch] = useState([]);
  useEffect(() => {
    if (!open) return;
    setBatch([]);
    setF({ famille_id: item?.famille_id || defaultFamilleId || familles[0]?.id || '', titre: item?.titre || '', description: item?.description || '', type: item?.type || 'book' });
    setMain(null); setPptx(null); setProgress(null); setShowPptx(!!item?.files?.pptx);
  }, [open, item, defaultFamilleId, familles]);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const pickMain = (file) => {
    setMain(file);
    if (file && !f.titre?.trim()) setF(p => ({ ...p, titre: titleFromName(file.name) }));
  };
  const pickMany = (files) => {
    if (files.length <= 1) { setBatch([]); pickMain(files[0] || null); return; }
    setMain(null);
    setBatch(prev => [...prev, ...files.map(file => ({ file, titre: titleFromName(file.name) }))]);
  };
  useEffect(() => {
    if (batch.length !== 1) return;
    setMain(batch[0].file); setF(p => ({ ...p, titre: batch[0].titre })); setBatch([]);
  }, [batch]);
  const saveBatch = async () => {
    let ok = 0;
    for (let i = 0; i < batch.length; i += 1) {
      const b = batch[i];
      try {
        const main_upload_id = await chunkedUpload(api, b.file, p => setProgress(`Fichier ${i + 1}/${batch.length} · ${p} %`));
        await api().post('/formation/items', { ...f, titre: b.titre.trim() || titleFromName(b.file.name), main_upload_id });
        ok += 1;
      } catch (e) { toast.error(`${b.file.name} : ${e?.response?.data?.detail || 'envoi impossible'}`); }
    }
    if (ok) { toast.success(`${ok} élément(s) ajouté(s)${f.type === 'video' ? ' — les vidéos s\'optimisent en arrière-plan' : ''}`); onSaved(); }
    else setProgress(null);
  };
  const save = async () => {
    if (batch.length > 1) { await saveBatch(); return; }
    try {
      setProgress('Envoi…');
      const main_upload_id = main ? await chunkedUpload(api, main, p => setProgress(`Fichier ${p} %`)) : null;
      const pptx_upload_id = pptx ? await chunkedUpload(api, pptx, p => setProgress(`PowerPoint ${p} %`)) : null;
      setProgress('Enregistrement…');
      const body = { ...f, main_upload_id, pptx_upload_id };
      if (item?.id) await api().put(`/formation/items/${item.id}`, body);
      else await api().post('/formation/items', body);
      toast.success(f.type === 'book' && main ? 'Élément enregistré — les slides se préparent, vous pouvez continuer' : 'Élément enregistré'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); setProgress(null); }
  };
  const busy = progress !== null;
  const isBatch = batch.length > 1;
  const canSave = isBatch ? f.famille_id && batch.every(b => b.titre.trim())
    : f.titre?.trim() && f.famille_id && (item?.id || main) && !(item?.id && f.type !== item.type && !main);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-surface max-w-lg" data-testid="item-dialog">
        <DialogHeader><DialogTitle className="text-ink">{item?.id ? "Modifier l'élément" : 'Ajouter un élément'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Famille *</Label>
              <select value={f.famille_id || ''} onChange={set('famille_id')} className="w-full h-9 px-3 rounded-md border border-border bg-surface text-sm" data-testid="item-famille">
                {familles.map(x => <option key={x.id} value={x.id}>{x.nom}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Type *</Label>
              <select value={f.type || 'book'} onChange={set('type')} className="w-full h-9 px-3 rounded-md border border-border bg-surface text-sm" data-testid="item-type">
                {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </div>
          </div>
          {!isBatch && <div className="space-y-1.5"><Label>Titre *</Label><Input value={f.titre || ''} onChange={set('titre')} data-testid="item-titre" /></div>}
          <div className="space-y-1.5"><Label>Descriptif{isBatch ? ' (commun à tous les fichiers)' : ''}</Label><Textarea rows={3} value={f.description || ''} onChange={set('description')} data-testid="item-description" /></div>
          <FilePick label={f.type === 'book' ? 'Fichier du diaporama (PDF) *' : item?.id ? 'Fichier *' : 'Fichier(s) * — vous pouvez en sélectionner plusieurs'} accept={TYPE_META[f.type || 'book'].accept}
            file={isBatch ? null : main} current={item?.files?.main?.name} summary={isBatch ? `${batch.length} fichiers sélectionnés — cliquez pour en ajouter` : null}
            onPick={item?.id ? pickMain : pickMany} multiple={!item?.id} testid="item-file-main" />
          {isBatch && (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="item-batch-list">
              {batch.map((b, i) => (
                <div key={`${b.file.name}-${i}`} className="flex items-center gap-2">
                  <Input value={b.titre} onChange={e => setBatch(prev => prev.map((x, j) => (j === i ? { ...x, titre: e.target.value } : x)))}
                    className="h-9 text-sm" data-testid={`item-batch-title-${i}`} />
                  <span className="text-[11px] text-text-3 whitespace-nowrap">{fmtSize(b.file.size)}</span>
                  <button type="button" onClick={() => setBatch(prev => prev.filter((_, j) => j !== i))} disabled={busy}
                    className="text-text-3 hover:text-red-600 px-1" data-testid={`item-batch-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
          {f.type === 'book' && (
            <>
              <p className="text-xs text-text-3">C'est ce fichier qui est diffusé. Dans PowerPoint : Fichier › Enregistrer sous › PDF — chaque page devient une slide.</p>
              {showPptx ? (
                <FilePick label="Version PowerPoint modifiable (facultatif)" accept=".pptx,.ppt" file={pptx} current={item?.files?.pptx?.name} onPick={setPptx} testid="item-file-pptx" />
              ) : (
                <button type="button" onClick={() => setShowPptx(true)} className="text-xs text-orange-700 underline" data-testid="item-add-pptx">
                  + Joindre aussi le .pptx pour que les commerciaux puissent le télécharger (facultatif)
                </button>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <BtnGhost onClick={onClose} disabled={busy}>Annuler</BtnGhost>
          <BtnPrimary onClick={save} disabled={busy || !canSave} data-testid="item-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? progress : isBatch ? `Ajouter les ${batch.length} fichiers` : 'Enregistrer'}
          </BtnPrimary>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
